import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon, XCircleIcon, ShieldCheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../api/axiosConfig';
import { toast } from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import PremiumTable from '../components/common/PremiumTable';

const SmtpConfigs = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const res = await api.get('/v1/admin/smtp-configs');
      if (res.data.success) {
        setConfigs(res.data.data);
      }
    } catch (error) {
      toast.error('Failed to load SMTP Configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDisabled = async (id, currentStatus) => {
    try {
      const res = await api.put(`/v1/admin/smtp-configs/${id}/toggle`, { adminDisabled: !currentStatus });
      if (res.data.success) {
        toast.success(res.data.message);
        setConfigs(configs.map(c => c._id === id ? { ...c, adminDisabled: !currentStatus } : c));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto animate-fade-in">
      <div className="flex justify-between items-start mb-8">
        <PageHeader 
          title="SMTP Configurations" 
          subtitle="Manage custom email setups for users and businesses." 
        />
        <button 
          onClick={() => navigate('/smtp-configs/create')}
          className="group flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white text-[14px] font-bold rounded-xl shadow-[0_4px_14px_0_rgba(220,38,38,0.39)] transition-all hover:-translate-y-0.5"
        >
          <PlusIcon className="w-5 h-5 transition-transform group-hover:rotate-90" />
          Add Credential
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex flex-col items-center justify-center text-gray-400">
            <svg className="animate-spin h-8 w-8 mb-4 text-red-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-sm font-medium">Loading configurations...</p>
          </div>
        ) : (
          <PremiumTable 
          title="SMTP Configurations"
          data={configs}
          primaryKey="_id"
          searchKeys={['businessId.name', 'email', 'host']}
          columns={[
            {
              header: 'Owner / Business',
              accessor: 'businessId.name',
              render: (config) => (
                config.businessId ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg">
                      {config.businessId.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{config.businessId.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Business</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center">
                      <ShieldCheckIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-bold text-purple-700">{config.userId?.name || 'Admin'}</p>
                      <p className="text-xs text-purple-400 mt-0.5">{config.isSystemDefault ? 'System Default' : 'Admin User'}</p>
                    </div>
                  </div>
                )
              )
            },
            {
              header: 'Email (Sender)',
              accessor: 'email',
              render: (config) => (
                <div className="flex items-center gap-2">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-700">{config.email}</span>
                </div>
              )
            },
            {
              header: 'Host & Port',
              accessor: 'host',
              render: (config) => (
                <div className="flex flex-col">
                  <span className="text-gray-900 font-medium">{config.host}</span>
                  <span className="text-xs text-gray-500 mt-0.5">Port: {config.port}</span>
                </div>
              )
            },
            {
              header: 'Status',
              accessor: 'isActive',
              render: (config) => (
                <div className="flex flex-col gap-1">
                  {config.adminDisabled ? (
                    <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 px-3 py-1 rounded-full text-[11px] font-bold border border-red-100 w-fit">
                      <XCircleIcon className="w-3.5 h-3.5" /> Suspended
                    </span>
                  ) : config.isActive ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full text-[11px] font-bold border border-emerald-100 w-fit">
                      <CheckCircleIcon className="w-3.5 h-3.5" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-[11px] font-bold border border-gray-200 w-fit">
                      User Disabled
                    </span>
                  )}
                </div>
              )
            },
            {
              header: 'Admin Action',
              render: (config) => (
                <button
                  onClick={() => handleToggleDisabled(config._id, config.adminDisabled)}
                  className={`px-4 py-2 rounded-lg text-[12px] font-bold transition-all ${
                    config.adminDisabled 
                      ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:shadow-sm' 
                      : 'bg-red-50 text-red-600 hover:bg-red-100 hover:shadow-sm'
                  }`}
                >
                  {config.adminDisabled ? 'Enable' : 'Suspend'}
                </button>
              )
            }
          ]}
        />
        )}
      </div>
    </div>
  );
};

export default SmtpConfigs;
